const DRAG_THRESHOLD = 6;
const FRICTION = 0.93;
const MIN_VELOCITY = 0.3;
const MAX_VELOCITY = 90;
const LINE_DELTA = 16;
const EDGE_TOLERANCE = 1;
const SNAP_RESTORE_DELAY = 160;
const COMPACT_QUERY = '(max-width: 749px)';
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

class AtelierLookbook extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;
  /** @type {HTMLElement | null} */
  #strip = null;
  /** @type {HTMLElement | null} */
  #fill = null;
  /** @type {HTMLElement | null} */
  #counter = null;
  /** @type {HTMLElement[]} */
  #frames = [];
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;
  /** @type {MediaQueryList | null} */
  #compact = null;
  /** @type {MediaQueryList | null} */
  #reduced = null;

  #renderFrame = 0;
  #inertiaFrame = 0;
  #snapTimer = 0;
  #index = -1;

  /** @type {number | null} */
  #pointerId = null;
  #dragging = false;
  #moved = false;
  #suppressClick = false;
  #originX = 0;
  #lastX = 0;
  #lastTime = 0;
  #originScroll = 0;
  #velocity = 0;

  connectedCallback() {
    if (this.#controller) return;

    const strip = this.querySelector('[data-lookbook-strip]');
    if (!(strip instanceof HTMLElement)) return;

    this.#strip = strip;
    this.#fill = this.querySelector('[data-lookbook-fill]');
    this.#counter = this.querySelector('[data-lookbook-current]');
    this.#frames = Array.from(strip.querySelectorAll('[data-lookbook-frame]'));
    this.#compact = window.matchMedia(COMPACT_QUERY);
    this.#reduced = window.matchMedia(REDUCED_MOTION_QUERY);

    this.#controller = new AbortController();
    const { signal } = this.#controller;

    strip.addEventListener('scroll', this.#onScroll, { passive: true, signal });
    strip.addEventListener('wheel', this.#onWheel, { passive: false, signal });
    strip.addEventListener('keydown', this.#onKeydown, { signal });
    strip.addEventListener('pointerdown', this.#onPointerDown, { signal });
    strip.addEventListener('pointermove', this.#onPointerMove, { signal });
    strip.addEventListener('pointerup', this.#onPointerUp, { signal });
    strip.addEventListener('pointercancel', this.#onPointerCancel, { signal });
    strip.addEventListener('lostpointercapture', this.#onPointerCancel, { signal });
    strip.addEventListener('click', this.#onClick, { capture: true, signal });
    strip.addEventListener('dragstart', this.#onDragStart, { signal });
    this.#compact.addEventListener('change', this.#onBreakpointChange, { signal });

    this.#resizeObserver = new ResizeObserver(this.#requestRender);
    this.#resizeObserver.observe(strip);

    this.#render();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;

    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;

    this.#stopInertia();

    if (this.#renderFrame) cancelAnimationFrame(this.#renderFrame);
    this.#renderFrame = 0;

    if (this.#snapTimer) clearTimeout(this.#snapTimer);
    this.#snapTimer = 0;

    this.#releasePointer();
    this.#strip?.classList.remove('is-dragging', 'is-grabbing');

    this.#strip = null;
    this.#fill = null;
    this.#counter = null;
    this.#frames = [];
    this.#compact = null;
    this.#reduced = null;
    this.#index = -1;
    this.#dragging = false;
    this.#moved = false;
    this.#suppressClick = false;
    this.#velocity = 0;
  }

  get #enhanced() {
    return this.#compact ? !this.#compact.matches : false;
  }

  get #maxScroll() {
    const strip = this.#strip;
    if (!strip) return 0;
    return Math.max(0, strip.scrollWidth - strip.clientWidth);
  }

  #onBreakpointChange = () => {
    this.#stopInertia();
    this.#releasePointer();
    this.#dragging = false;
    this.#moved = false;
    this.#strip?.classList.remove('is-dragging', 'is-grabbing');
    this.#requestRender();
  };

  #onScroll = () => {
    this.#requestRender();
  };

  #requestRender = () => {
    if (this.#renderFrame) return;
    this.#renderFrame = requestAnimationFrame(() => {
      this.#renderFrame = 0;
      this.#render();
    });
  };

  #render() {
    const strip = this.#strip;
    if (!strip) return;

    const max = this.#maxScroll;
    const progress = max > EDGE_TOLERANCE ? Math.min(1, Math.max(0, strip.scrollLeft / max)) : 1;
    (this.#fill ?? this).style.setProperty('--lookbook-progress', String(progress));

    if (this.#frames.length === 0) return;

    const index = this.#currentIndex(max);
    if (index === this.#index) return;
    this.#index = index;

    if (this.#counter) this.#counter.textContent = String(index + 1).padStart(2, '0');
  }

  /** @param {number} max */
  #currentIndex(max) {
    const strip = this.#strip;
    const total = this.#frames.length;
    if (!strip || total === 0) return 0;
    if (max <= EDGE_TOLERANCE || strip.scrollLeft >= max - EDGE_TOLERANCE) return total - 1;

    const edge = strip.scrollLeft + EDGE_TOLERANCE * 8;
    const index = this.#frames.findIndex((frame) => frame.offsetLeft + frame.offsetWidth > edge);
    return index === -1 ? total - 1 : index;
  }

  /** @param {WheelEvent} event */
  #onWheel = (event) => {
    const strip = this.#strip;
    if (!strip || !this.#enhanced) return;
    if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;

    const max = this.#maxScroll;
    if (max <= EDGE_TOLERANCE) return;

    const delta = this.#normalizeDelta(event);
    if (delta === 0) return;

    const atStart = strip.scrollLeft <= EDGE_TOLERANCE;
    const atEnd = strip.scrollLeft >= max - EDGE_TOLERANCE;
    if ((delta < 0 && atStart) || (delta > 0 && atEnd)) return;

    event.preventDefault();
    this.#stopInertia();
    strip.scrollLeft = Math.min(max, Math.max(0, strip.scrollLeft + delta));
  };

  /** @param {WheelEvent} event */
  #normalizeDelta(event) {
    const strip = this.#strip;
    if (event.deltaMode === 1) return event.deltaY * LINE_DELTA;
    if (event.deltaMode === 2) return event.deltaY * (strip ? strip.clientWidth : LINE_DELTA);
    return event.deltaY;
  }

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    const strip = this.#strip;
    if (!strip) return;

    const max = this.#maxScroll;
    const step = this.#step();
    let target = strip.scrollLeft;

    switch (event.key) {
      case 'ArrowRight':
        target = strip.scrollLeft + step;
        break;
      case 'ArrowLeft':
        target = strip.scrollLeft - step;
        break;
      case 'Home':
        target = 0;
        break;
      case 'End':
        target = max;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.#stopInertia();

    const behavior = this.#reduced?.matches ? 'auto' : 'smooth';
    strip.scrollTo({ left: Math.min(max, Math.max(0, target)), behavior });
  };

  #step() {
    const strip = this.#strip;
    if (!strip) return 0;
    const frame = this.#frames[Math.max(0, this.#index)] ?? this.#frames[0];
    if (!frame) return strip.clientWidth * 0.8;
    const gap = parseFloat(getComputedStyle(strip).columnGap) || 0;
    return frame.offsetWidth + gap;
  }

  /** @param {PointerEvent} event */
  #onPointerDown = (event) => {
    const strip = this.#strip;
    if (!strip || !this.#enhanced) return;
    if (event.pointerType !== 'mouse' || event.button !== 0) return;

    this.#stopInertia();

    this.#pointerId = event.pointerId;
    this.#dragging = true;
    this.#moved = false;
    this.#suppressClick = false;
    this.#originX = event.clientX;
    this.#lastX = event.clientX;
    this.#lastTime = event.timeStamp;
    this.#originScroll = strip.scrollLeft;
    this.#velocity = 0;

    strip.setPointerCapture(event.pointerId);
  };

  /** @param {PointerEvent} event */
  #onPointerMove = (event) => {
    const strip = this.#strip;
    if (!strip || !this.#dragging || event.pointerId !== this.#pointerId) return;

    const travel = event.clientX - this.#originX;

    if (!this.#moved) {
      if (Math.abs(travel) < DRAG_THRESHOLD) return;
      this.#moved = true;
      strip.classList.add('is-dragging', 'is-grabbing');
    }

    const elapsed = event.timeStamp - this.#lastTime;
    if (elapsed > 0) {
      const velocity = ((event.clientX - this.#lastX) / elapsed) * LINE_DELTA;
      this.#velocity = Math.max(-MAX_VELOCITY, Math.min(MAX_VELOCITY, velocity));
      this.#lastX = event.clientX;
      this.#lastTime = event.timeStamp;
    }

    const max = this.#maxScroll;
    strip.scrollLeft = Math.min(max, Math.max(0, this.#originScroll - travel));
  };

  /** @param {PointerEvent} event */
  #onPointerUp = (event) => {
    if (!this.#dragging || event.pointerId !== this.#pointerId) return;

    const moved = this.#moved;
    this.#dragging = false;
    this.#releasePointer();
    this.#strip?.classList.remove('is-grabbing');

    if (!moved) {
      this.#strip?.classList.remove('is-dragging');
      return;
    }

    this.#suppressClick = true;

    if (this.#reduced?.matches || Math.abs(this.#velocity) < MIN_VELOCITY) {
      this.#settle();
      return;
    }

    this.#inertiaFrame = requestAnimationFrame(this.#glide);
  };

  /** @param {PointerEvent} event */
  #onPointerCancel = (event) => {
    if (event.pointerId !== this.#pointerId) return;
    this.#dragging = false;
    this.#moved = false;
    this.#releasePointer();
    this.#settle();
  };

  #glide = () => {
    const strip = this.#strip;
    if (!strip) return;

    this.#velocity *= FRICTION;

    const max = this.#maxScroll;
    const next = Math.min(max, Math.max(0, strip.scrollLeft - this.#velocity));
    const stalled = next === strip.scrollLeft;
    strip.scrollLeft = next;

    if (stalled || Math.abs(this.#velocity) < MIN_VELOCITY) {
      this.#inertiaFrame = 0;
      this.#velocity = 0;
      this.#settle();
      return;
    }

    this.#inertiaFrame = requestAnimationFrame(this.#glide);
  };

  #settle() {
    this.#strip?.classList.remove('is-grabbing');

    if (this.#snapTimer) clearTimeout(this.#snapTimer);
    this.#snapTimer = setTimeout(() => {
      this.#snapTimer = 0;
      this.#strip?.classList.remove('is-dragging');
      this.#moved = false;
    }, SNAP_RESTORE_DELAY);
  }

  #stopInertia() {
    if (this.#inertiaFrame) cancelAnimationFrame(this.#inertiaFrame);
    this.#inertiaFrame = 0;
    this.#velocity = 0;
  }

  #releasePointer() {
    const strip = this.#strip;
    if (strip && this.#pointerId !== null && strip.hasPointerCapture(this.#pointerId)) {
      strip.releasePointerCapture(this.#pointerId);
    }
    this.#pointerId = null;
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    if (!this.#suppressClick) return;
    this.#suppressClick = false;
    event.preventDefault();
    event.stopPropagation();
  };

  /** @param {DragEvent} event */
  #onDragStart = (event) => {
    if (this.#enhanced) event.preventDefault();
  };
}

if (!customElements.get('atelier-lookbook-scroller')) {
  customElements.define('atelier-lookbook-scroller', AtelierLookbook);
}
